import { getCmsArticleSlug } from '@/lib/cms-guides';
import type { CmsArticle } from '@/lib/cms-types';

export const GUIDE_FEEDBACK_STORAGE_KEY = 'xat-help-guide-feedback-v1';

export const GUIDE_NEGATIVE_FEEDBACK_MAX_LENGTH = 500;
export const GUIDE_XAT_USERNAME_MAX_LENGTH = 32;

export type GuideVoteStats = {
  likes: number;
  dislikes: number;
};

export type GuideNegativeFeedback = {
  id: string;
  guideKey: string;
  categoryId: string;
  slug: string;
  articleTitle: string;
  message: string;
  /** Username no xat informado pelo visitante (opcional). */
  xatUsername?: string | null;
  createdAt: string;
};

export type GuideFeedbackState = {
  stats: Record<string, GuideVoteStats>;
  negativeFeedback: GuideNegativeFeedback[];
  /** guideKey → voto do visitante neste browser */
  voterVotes: Record<string, 'like' | 'dislike'>;
};

function emptyState(): GuideFeedbackState {
  return { stats: {}, negativeFeedback: [], voterVotes: {} };
}

export function buildGuideFeedbackKey(categoryId: string, slug: string): string {
  return `${categoryId}:${slug.trim().toLowerCase()}`;
}

export function buildGuideFeedbackKeyFromArticle(article: CmsArticle): string {
  return buildGuideFeedbackKey(article.categoryId, getCmsArticleSlug(article));
}

export function normalizeXatUsername(raw: string): string {
  return raw.trim().replace(/^@+/, '').slice(0, GUIDE_XAT_USERNAME_MAX_LENGTH);
}

export function loadGuideFeedbackState(): GuideFeedbackState {
  if (typeof window === 'undefined') return emptyState();
  try {
    const raw = localStorage.getItem(GUIDE_FEEDBACK_STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as Partial<GuideFeedbackState>;
    return {
      stats: parsed.stats && typeof parsed.stats === 'object' ? parsed.stats : {},
      negativeFeedback: Array.isArray(parsed.negativeFeedback) ? parsed.negativeFeedback : [],
      voterVotes: parsed.voterVotes && typeof parsed.voterVotes === 'object' ? parsed.voterVotes : {},
    };
  } catch {
    return emptyState();
  }
}

export function saveGuideFeedbackState(state: GuideFeedbackState): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(GUIDE_FEEDBACK_STORAGE_KEY, JSON.stringify(state));
}

export function getGuideVoteStats(state: GuideFeedbackState, guideKey: string): GuideVoteStats {
  return state.stats[guideKey] ?? { likes: 0, dislikes: 0 };
}

export function getVisitorVote(state: GuideFeedbackState, guideKey: string): 'like' | 'dislike' | null {
  return state.voterVotes[guideKey] ?? null;
}

export function recordGuideLike(state: GuideFeedbackState, guideKey: string): GuideFeedbackState {
  if (state.voterVotes[guideKey]) return state;
  const current = getGuideVoteStats(state, guideKey);
  return {
    ...state,
    stats: {
      ...state.stats,
      [guideKey]: { ...current, likes: current.likes + 1 },
    },
    voterVotes: { ...state.voterVotes, [guideKey]: 'like' },
  };
}

export function recordGuideDislikeWithMessage(
  state: GuideFeedbackState,
  input: {
    guideKey: string;
    categoryId: string;
    slug: string;
    articleTitle: string;
    message: string;
    xatUsername?: string;
  }
): GuideFeedbackState {
  const trimmed = input.message.trim().slice(0, GUIDE_NEGATIVE_FEEDBACK_MAX_LENGTH);
  if (!trimmed) return state;

  const xatUsername = normalizeXatUsername(input.xatUsername ?? '');

  const alreadyDisliked = state.voterVotes[input.guideKey] === 'dislike';
  const current = getGuideVoteStats(state, input.guideKey);

  const negativeFeedback: GuideNegativeFeedback = {
    id: crypto.randomUUID(),
    guideKey: input.guideKey,
    categoryId: input.categoryId,
    slug: input.slug,
    articleTitle: input.articleTitle,
    message: trimmed,
    ...(xatUsername ? { xatUsername } : {}),
    createdAt: new Date().toISOString(),
  };

  return {
    ...state,
    stats: alreadyDisliked
      ? state.stats
      : {
          ...state.stats,
          [input.guideKey]: { ...current, dislikes: current.dislikes + 1 },
        },
    voterVotes: alreadyDisliked
      ? state.voterVotes
      : { ...state.voterVotes, [input.guideKey]: 'dislike' },
    negativeFeedback: [negativeFeedback, ...state.negativeFeedback],
  };
}

export function countNegativeFeedbackForGuide(
  state: GuideFeedbackState,
  guideKey: string
): number {
  return state.negativeFeedback.filter((f) => f.guideKey === guideKey).length;
}

export type GuideFeedbackSummary = {
  likes: number;
  dislikes: number;
  messages: number;
  guidesWithMessages: number;
  /** 0–100 ou null se ainda não há votos */
  satisfactionRate: number | null;
};

export function aggregateGuideFeedbackStats(state: GuideFeedbackState): GuideFeedbackSummary {
  let likes = 0;
  let dislikes = 0;
  for (const stats of Object.values(state.stats)) {
    likes += stats.likes;
    dislikes += stats.dislikes;
  }
  const messages = state.negativeFeedback.length;
  const guidesWithMessages = new Set(state.negativeFeedback.map((f) => f.guideKey)).size;
  const totalVotes = likes + dislikes;
  const satisfactionRate = totalVotes > 0 ? Math.round((likes / totalVotes) * 100) : null;
  return { likes, dislikes, messages, guidesWithMessages, satisfactionRate };
}
